// AUDIO_LINKED_PROVISIONAL behaves the same as AUDIO_LINKED (audio is
// available) but the record still lives in the "hot bucket" rather than
// having settled into the main store alongside the rest.
export type CallStatus = "METADATA_CAPTURED" | "AUDIO_LINKED" | "AUDIO_LINKED_PROVISIONAL";

export type CallDirection = "inbound" | "outbound" | "internal";

// call_records.transcription_status / summary_status. null means the call
// hasn't been queued for that step yet.
export type ProcessingStatus = "PENDING" | "FAILED" | "COMPLETE";

// Mirrors the `call_records` table columns 1:1 (snake_case, backend casing).
export interface CallRecord {
  // Also the call centre PBX's channel id ("<unix-seconds>.<sequence>") -
  // an unambiguous UTC call-start timestamp, used for that in callRow.tsx.
  call_id: string;
  tenant: string;
  status: CallStatus;

  // call_date/call_time/call_timestamp are stamped with a literal "Z" but
  // are actually Europe/Madrid (call centre) local time, not UTC - see the
  // conversion in callRow.tsx's formatDateTime.
  call_date: string;
  call_time: string;
  call_timestamp: string;

  calling_name: string | null;
  extension: string | null;
  direction: CallDirection | null;

  called_number: string | null;
  called_number_normalised: string | null;

  caller_id: string | null;
  caller_id_normalised: string | null;

  duration: number;
  call_rating_duration: number;
  call_cost: number;

  s3_audio_uri: string | null;
  call_end_time: string | null;
  processed_at: string;

  transcription_status: ProcessingStatus | null;
  summary_status: ProcessingStatus | null;
}

// Mirrors CallQueryParams. All optional except limit/offset, which
// useCallsList always supplies.
export interface CallQueryParams {
  tenant?: string;
  status?: CallStatus;
  direction?: CallDirection;
  calling_name?: string;
  extension?: string;
  // Accept either a raw or normalised number (backend normalises and
  // searches the normalised column either way), and comma-separate to
  // search multiple numbers in one request.
  caller_id?: string;
  called_number?: string;
  start_time?: string;
  end_time?: string;
  limit: number;
  offset: number;
}

export type CallFilters = Omit<CallQueryParams, "limit" | "offset">;

export interface ListCallsResponse {
  // Number of records in this page (== data.length).
  count: number;
  // Total matching records across all pages. Only computed/returned when
  // offset === 0 - the checkpointed end_time already pins the result set
  // for later pages, so it's omitted there to save the backend re-counting
  // on every "load more".
  total_count?: number;
  // True if total_count was capped for performance (currently at 10,000,
  // but that threshold may change) rather than being an exact count - in
  // that case total_count holds the cap itself, and the UI should render
  // it as "<cap>+" rather than an exact number.
  total_count_capped?: boolean;
  limit: number;
  offset: number;
  data: CallRecord[];
}

// Shape of `/data-warehouse/agents/list` wasn't specified up front - kept
// loose and normalised defensively in calls.service.ts until confirmed
// against the live response.
export interface Agent {
  name: string;
  active?: boolean;
  [key: string]: unknown;
}

export interface SignedUrlResponse {
  call_id: string;
  signed_url: string;
}

// Response of `data-remote/number-associated-with/{phone_number}` - Zoho CRM
// Accounts ("Customers"), Contacts and Leads records holding that number.
export type ZohoAssociatedModule = "Accounts" | "Contacts" | "Leads";

export interface NumberAssociationMatch {
  module: ZohoAssociatedModule;
  id: string;
  // Which phone fields the number was found in, e.g. ["Mobile", "Phone"]
  fields_matched: string[];
  // Display identifiers, keyed by label, e.g. { "Full Name": "Jane Doe" }.
  // Keys vary per module, so render them generically. Lookup fields come
  // back as Zoho lookup objects (e.g. a Contact's "Customer Name" is the
  // linked Account's { name, id }) rather than plain strings.
  identity: Record<string, NumberAssociationIdentityValue>;
}

export type NumberAssociationIdentityValue =
  string | number | { name: string | null; id: string } | null;

export interface NumberAssociationGroup {
  count: number;
  matches: NumberAssociationMatch[];
}

// Raw API shape: results grouped per module
export interface NumberAssociationRawResponse {
  accounts?: NumberAssociationGroup;
  contacts?: NumberAssociationGroup;
  leads?: NumberAssociationGroup;
}

// Flattened across modules (Accounts, Contacts, Leads order) by
// CallsService.getNumberAssociations - what the UI consumes.
export type NumberAssociationResponse = NumberAssociationGroup;

export interface TranscriptWord {
  start: number;
  end: number;
  word: string;
}

// Segments come back grouped/interleaved by speaker rather than sorted by
// start time, and can overlap (people talking over each other).
export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  // "Agent" | "Customer" in practice
  speaker: string;
  words: TranscriptWord[];
}

export interface CallTranscriptionResponse {
  call_id: string;
  transcription_status: ProcessingStatus | null;
  transcription: TranscriptSegment[] | null;
  transcription_raw: string | null;
  created_at: string;
  updated_at: string;
}

// Summariser's reply when there's nothing worth summarising (voicemail, no
// answer, dropped call...) - stored verbatim as the summary text.
export const NO_USEFUL_CONTENT_SUMMARY = "NO_USEFUL_CONTENT";

// Response of `data-warehouse/calls/{call_id}/summary` - plain text, up to
// ~60 words.
export interface CallSummaryResponse {
  call_id: string;
  summary_status: ProcessingStatus | null;
  summary: string | null;
  created_at: string | null;
  updated_at: string | null;
}
